import { Flag } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Divider,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { getLatestIteration } from '../../../lib/utils';
import SkeletonLoader from '../../elements/SkeletonLoader';
import { CustomChip } from '../../../ui/primitives/Chips';
import ActionButton from '../../../ui/primitives/ActionButton';
import CustomSelect from '../../../ui/primitives/CustomSelect';
import CustomAvatar from '../../elements/CustomAvatar';
import { tokens } from '../../../mui-theme';

const VIEW_MODE_OPTIONS = [
  { id: 'iterations', name: 'Iterations' },
  { id: 'testcases', name: 'Test Cases' },
];

const TestPlayerSidebar = ({
  testCases,
  activeTab,
  handleTabClick,
  users,
  loading,
  viewMode,
  handleViewModeChange,
  iterations,
  loadMoreTestCases,
  hasMoreTestCases,
}) => {
  const getDisplayName = (accountId) => {
    const user = users.find((user) => user?.accountId === accountId);
    return user ? user.displayName : accountId;
  };

  const getAvatarProps = (accountId) => {
    const displayName = getDisplayName(accountId) || '';
    const parts = displayName.trim().split(' ');
    return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' };
  };

  const latestIteration = getLatestIteration(iterations);

  return (
    <Box className="mb-3" sx={{ width: 180, flex: '0 0 200px', overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* Group By selector */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Group By:
        </Typography>
        <Box sx={{ minWidth: 120 }}> 
          <CustomSelect
            id="viewMode"
            value={viewMode}
            onChange={(val) => handleViewModeChange(val)}
            options={VIEW_MODE_OPTIONS}
          />
        </Box>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {loading && testCases.length === 0 ? (
        <SkeletonLoader />
      ) : viewMode === 'testcases' ? (
        <>
          {testCases?.map(({ testcase, assigned_to, id }) => (
            <Box key={id} sx={{ mb: 1, width: '100%', boxSizing: 'border-box' }}>  
              <Tooltip title={testcase?.summary || ''} arrow placement="right">
                <ButtonBase
                  onClick={() => handleTabClick(id, testcase)}
                  sx={{
                    width: '100%',        
                    boxSizing: 'border-box',
                    textAlign: 'left',
                    display: 'block',
                    p: 1.25,            
                    borderRadius: 1.5,    
                    border: '1.5px solid',
                    borderColor: activeTab === id ? 'primary.main' : 'divider',
                    backgroundColor: activeTab === id ? 'action.selected' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                    '&:hover': {
                      backgroundColor: activeTab === id ? 'action.selected' : 'action.hover',
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}> 
                      <Flag sx={{ color: testcase?.priority_details?.color, fontSize: 18 }} />  
                      <Typography variant="body2" fontWeight={600}>  
                        {testcase?.key}
                      </Typography>
                    </Stack>
                    <CustomAvatar {...getAvatarProps(assigned_to)} size={22} showTitle fallback="G" />  
                  </Stack>
                </ButtonBase>
              </Tooltip>
            </Box>
          ))}

          {hasMoreTestCases && (
            <Box sx={{ textAlign: 'left' }}>
              <ActionButton
                intent="ghost"
                size="small"
                label={loading ? 'Loading More...' : 'Show More'}
                loading={loading}
                disabled={loading}
                onClick={loadMoreTestCases}
              />
            </Box>
          )}
        </>
      ) : (
        iterations.map((iteration) => {
          const isActive = activeTab === iteration.id;
          const isLatest = latestIteration?.id === iteration.id;
          const isCompleted = iteration.is_closed;

          return (
            <Box key={iteration.id} sx={{ mb: 1 }}> 
              <ButtonBase
                onClick={() => handleTabClick(iteration.id)}
                sx={{
                  width: '100%',         
                  boxSizing: 'border-box',
                  textAlign: 'left',
                  display: 'block',
                  p: 1.25,               
                  borderRadius: 1.5,       
                  border: '1.5px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  backgroundColor: isActive
                    ? (theme) => alpha(theme.palette.primary.main, 0.06)
                    : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  '&:hover': {
                    backgroundColor: isActive
                      ? (theme) => alpha(theme.palette.primary.main, 0.06)
                      : 'action.hover',
                  },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="body2"  
                    color="text.secondary"
                    title={iteration?.name}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mr: 0.5,          
                    }}
                  >
                    {iteration?.name}
                  </Typography>
                  <CustomAvatar {...getAvatarProps(iteration.executed_by)} size={22} showTitle fallback="G" backgroundColor={tokens.colors.neutral[400]} />
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 0.75 }}    
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    title={[
                      iteration?.environment_details?.name,
                      iteration?.country?.length > 0 ? iteration.country.join(', ') : null,
                    ]
                      .filter(Boolean)
                      .join(' | ')}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mr: 0.5,         
                    }}
                  >
                    {iteration?.environment_details?.name}
                    {iteration?.country?.length > 0 && ` | ${iteration?.country?.join(', ')}`}
                  </Typography>

                  <Stack direction="row" spacing={0.5} flexShrink={0}>
                    {isCompleted ? (
                      <CustomChip
                        label="Completed"
                        size="small"
                        title="This iteration is closed"
                        sx={(theme) => ({
                          backgroundColor: alpha(theme.palette.success.main, 0.12),
                          color: theme.palette.success.main,
                        })}
                      />
                    ) : isLatest ? (
                      <CustomChip label="New" title="Latest Iteration" size="small" />
                    ) : null}
                  </Stack>
                </Stack>
              </ButtonBase>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default TestPlayerSidebar;